import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  ActivityHistoryCard,
  ActivityHistoryDayGroup,
  ActivityHistoryDialog,
  ActivityHistoryEmpty,
  ActivityHistoryFilterBar,
  ActivityHistorySelect,
  ActivityHistorySkeleton,
} from '../../Components/ActivityHistory/ActivityHistoryUi';
import { appModalSecondaryButtonSx } from '../../Components/appModalChrome.styles';
import { colors } from '../../styles/theme';
import { getMetaWriteLog } from '../../services/metaAdsService';
import { metaTodayCordoba } from '../metaPeriod';
import {
  META_WRITE_LOG_DEFAULT_PERIOD, META_WRITE_LOG_LEVELS, META_WRITE_LOG_PAGE, META_WRITE_LOG_PERIODS, filterMetaWriteLogItems,
  metaWriteLogDetails, metaWriteLogRange, readMetaWriteLog, readMetaWriteLogError,
} from '../metaWriteLog';
import { formatCollectionActivityClock, groupCollectionActivityByDay } from '../../Map/utils/collectionActivityDisplay';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';

/*
 * F2b · HISTORIAL de cambios hechos desde Terrio en Meta (`getmetawritelog`). Solo el dueño: se abre
 * desde ⚙. Misma estructura y estilos que el historial del detalle de una lista (`ActivityHistoryUi`):
 * buscador (filtra lo cargado: la API no busca) + nivel + período, campaña aparte, grupos por día y
 * tarjetas con el detalle desplegable. 20 por página con «Ver más» (cursor). Nunca rompe el panel:
 * sin la ruta muestra «Todavía no disponible».
 */

const RESULT_TONES = {
  error: { color: colors.error, bg: 'rgba(242,184,181,0.12)' },
  warn: { color: '#FFD2A8', bg: 'rgba(255,152,0,0.14)' },
  info: { color: '#CFE6E5', bg: 'rgba(72,146,145,0.18)' },
};

const ICON_BY_ACTION = {
  pause: PauseCircleOutlineIcon,
  activate: PlayCircleOutlineIcon,
  rename: DriveFileRenameOutlineIcon,
  budget: PaymentsOutlinedIcon,
  end_time: EventOutlinedIcon,
  archive: ArchiveOutlinedIcon,
  delete: DeleteOutlineIcon,
  // F2b API: «Creada desde Terrio».
  create: AddCircleOutlineIcon,
};

const ROLE_LABELS = { owner: 'Dueño', editor: 'Editor' };
const toOptions = (list) => list.map((o) => ({ id: o.key, label: o.label }));
const LEVEL_OPTIONS = toOptions(META_WRITE_LOG_LEVELS);
const PERIOD_OPTIONS = toOptions(META_WRITE_LOG_PERIODS);

function ResultChip({ view }) {
  if (!view) return null;
  const t = RESULT_TONES[view.tone] || RESULT_TONES.info;
  const chip = (
    <Box component="span" tabIndex={view.tooltip ? 0 : undefined} sx={{ fontSize: '11px', fontWeight: 600, px: 1, py: '1px', borderRadius: '999px', color: t.color, bgcolor: t.bg, whiteSpace: 'nowrap', flexShrink: 0 }}>
      {view.label}
    </Box>
  );
  return view.tooltip ? <Tooltip title={<span>{view.tooltip}</span>} placement="top" arrow>{chip}</Tooltip> : chip;
}

/**
 * @param {{ open: boolean, onClose: () => void, campaigns?: Array<{ id: string, name: string }>,
 *   initialCampaignId?: string }} props
 */
export default function MetaWriteLogDialog({ open, onClose, campaigns = [], initialCampaignId = '' }) {
  const [campaignId, setCampaignId] = useState(initialCampaignId || '');
  const [level, setLevel] = useState('');
  const [period, setPeriod] = useState(META_WRITE_LOG_DEFAULT_PERIOD);
  const [query, setQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [state, setState] = useState({ status: 'loading', items: [], nextCursor: null, hasMore: false, error: null, more: false });
  const abortRef = useRef(null);
  const { scrollRef, fadeTop, fadeBottom, refreshScrollEdges } = useScrollEdgeFade();

  const load = useCallback(async ({ cursor = null } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => (cursor ? { ...prev, more: true, moreError: null } : { status: 'loading', items: [], nextCursor: null, hasMore: false, error: null, more: false }));
    const { from, to } = metaWriteLogRange(period, metaTodayCordoba());
    let res;
    try {
      res = await getMetaWriteLog({ campaignId, level, from, to, limit: META_WRITE_LOG_PAGE, cursor, signal: controller.signal });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      res = null;
    }
    if (controller.signal.aborted) return;
    if (!res?._httpOk) {
      const error = readMetaWriteLogError(res);
      setState((prev) => (cursor ? { ...prev, more: false, moreError: error.message } : { status: 'error', items: [], nextCursor: null, hasMore: false, error, more: false }));
      return;
    }
    const page = readMetaWriteLog(res);
    setState((prev) => ({
      status: 'ready',
      items: cursor ? [...prev.items, ...page.items] : page.items,
      nextCursor: page.nextCursor,
      hasMore: page.hasMore,
      error: null,
      more: false,
    }));
  }, [campaignId, level, period]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return () => abortRef.current?.abort();
  }, [open, load]);

  const shown = useMemo(() => filterMetaWriteLogItems(state.items, query), [state.items, query]);
  const dayGroups = useMemo(
    () => groupCollectionActivityByDay(shown.map((item) => ({ ...item, created_at: item.createdAt }))),
    [shown],
  );
  useEffect(() => { refreshScrollEdges(); }, [shown, expandedIds, state.status, refreshScrollEdges]);

  if (!open) return null;
  const toggle = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const campaignName = campaigns.find((c) => c.id === campaignId)?.name || '';
  const campaignOptions = [{ id: '', label: 'Todas las campañas' }, ...campaigns.map((c) => ({ id: c.id, label: c.name }))];

  let body;
  if (state.status === 'loading') {
    body = <Box data-testid="meta-write-log-loading" aria-busy="true"><ActivityHistorySkeleton /></Box>;
  } else if (state.status === 'error') {
    const retry = state.error?.kind === 'error' || state.error?.kind === 'validation';
    body = (
      <ActivityHistoryEmpty
        message={state.error?.message}
        actionLabel={retry ? 'Reintentar' : ''}
        onAction={() => load()}
        testId={`meta-write-log-${state.error?.kind || 'error'}`}
      />
    );
  } else if (!shown.length) {
    const filtered = Boolean(query.trim()) || Boolean(level) || Boolean(campaignId);
    let message = 'Todavía no hay cambios.';
    if (state.items.length || filtered) message = 'No hay cambios con estos filtros.';
    else if (period !== 'all') message = `No hay cambios en los ${PERIOD_OPTIONS.find((o) => o.id === period)?.label.toLowerCase() || 'últimos días'}.`;
    body = (
      <ActivityHistoryEmpty
        message={message}
        actionLabel={period !== 'all' ? 'Ver todo el tiempo' : ''}
        onAction={() => setPeriod('all')}
        testId="meta-write-log-empty"
      />
    );
  } else {
    body = (
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }} data-testid="meta-write-log-list">
          {dayGroups.map((group) => (
            <ActivityHistoryDayGroup key={group.key} label={group.label}>
              {group.rows.map((item) => (
                <ActivityHistoryCard
                  key={item.id}
                  icon={ICON_BY_ACTION[item.action] || HistoryOutlinedIcon}
                  title={item.title}
                  badge={<ResultChip view={item.resultView} />}
                  time={formatCollectionActivityClock(item.createdAt)}
                  actor={item.actorName || ''}
                  roleKey={item.actorRole}
                  roleLabel={ROLE_LABELS[item.actorRole] || ''}
                  summary={item.summary}
                  details={metaWriteLogDetails(item)}
                  expanded={expandedIds.has(item.id)}
                  onToggle={() => toggle(item.id)}
                  testId={`meta-write-log-item-${item.id}`}
                  dataAttrs={{ 'data-result': item.result }}
                />
              ))}
            </ActivityHistoryDayGroup>
          ))}
        </Box>
        {state.hasMore ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0, pb: 0.5 }}>
            <Button
              variant="outlined"
              onClick={() => load({ cursor: state.nextCursor })}
              disabled={state.more}
              startIcon={state.more ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : null}
              sx={appModalSecondaryButtonSx}
              data-testid="meta-write-log-more"
            >
              Ver más
            </Button>
          </Box>
        ) : null}
      </>
    );
  }

  return (
    <ActivityHistoryDialog
      open={open}
      onClose={onClose}
      paperTestId="meta-write-log-dialog"
      listTestId="meta-write-log-body"
      subtitle={campaignName
        ? `Cambios hechos desde Terrio en la campaña ${campaignName}.`
        : 'Cambios hechos desde Terrio en tus campañas de Meta.'}
      error={state.moreError || ''}
      listRef={scrollRef}
      listSx={getScrollEdgeFadeSx(fadeTop, fadeBottom)}
      busy={state.status === 'loading' || state.more}
      filters={(
        <>
          <ActivityHistoryFilterBar
            query={query}
            onQueryChange={setQuery}
            placeholder="Buscar por campaña, conjunto o anuncio"
            testId="meta-write-log-search"
          >
            <ActivityHistorySelect value={level} onChange={setLevel} options={LEVEL_OPTIONS} ariaLabel="Nivel" sx={{ minWidth: 112 }} testId="meta-write-log-level" />
            <ActivityHistorySelect value={period} onChange={setPeriod} options={PERIOD_OPTIONS} ariaLabel="Período" testId="meta-write-log-period" />
          </ActivityHistoryFilterBar>
          {campaigns.length ? (
            <ActivityHistorySelect value={campaignId} onChange={setCampaignId} options={campaignOptions} ariaLabel="Campaña" sx={{ width: '100%' }} testId="meta-write-log-campaign" />
          ) : null}
        </>
      )}
    >
      {body}
    </ActivityHistoryDialog>
  );
}
